import { AnimatePresence, motion } from 'framer-motion';
import { Clock, Send, Star, Users, Share2, Download, Upload, MessageSquare, GitBranch, Bot } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Area, AreaChart, Bar, BarChart as RechartsBarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '../../lib/utils';

// =============================================
// Demo Configuration
// =============================================

const sampleCode = `import pandas as pd
import plotly.express as px
import preswald

# Load the dataset
df = pd.read_csv('data/sales.csv')

# Process the data
monthly_sales = df.groupby(['date', 'category'])['revenue'].sum().reset_index()

# Create an interactive visualization
fig = px.line(monthly_sales, 
              x='date', 
              y='revenue',
              color='category',
              title='Monthly Sales by Category')

# Display in Preswald
preswald.plotly(fig)`;

// =============================================
// Simulated Code Editor Component
// =============================================

const SimulatedVisualEditor = () => {
  const [typedCode, setTypedCode] = React.useState('');
  const [currentLine, setCurrentLine] = React.useState(0);
  const codeLines = sampleCode.split('\n');

  React.useEffect(() => {
    if (currentLine >= codeLines.length) {
      // Reset animation after completion
      const timer = setTimeout(() => {
        setTypedCode('');
        setCurrentLine(0);
      }, 3000);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setTypedCode(prev => prev + (prev ? '\n' : '') + codeLines[currentLine]);
      setCurrentLine(prev => prev + 1);
    }, 200);

    return () => clearTimeout(timer);
  }, [currentLine]);

  return (
    <div className="h-full">
      <div className="flex h-full divide-x border rounded-lg overflow-hidden">
        {/* File Tree */}
        <div className="w-48 bg-gray-50/50 font-mono text-sm">
          <div className="p-2 border-b bg-white">
            <div className="text-xs text-muted-foreground font-medium">Explorer</div>
          </div>
          <div className="p-2">
            <div className="flex items-center gap-1 text-muted-foreground mb-2">
              <span>📁</span>
              <span className="font-medium">project</span>
            </div>
            <div className="pl-3 space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground/80">
                <span>📁</span>
                <span>data</span>
              </div>
              <div className="pl-3 flex items-center gap-1 text-muted-foreground/70">
                <span>📊</span>
                <span>sales.csv</span>
              </div>
              <div className="flex items-center gap-1 text-primary font-medium">
                <span>🐍</span>
                <span>sales_dashboard.py</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground/80">
                <span>⚙️</span>
                <span>preswald.toml</span>
              </div>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col bg-white">
          <div className="border-b px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">sales_dashboard.py</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-2 py-1 text-xs rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors">
                Format
              </button>
              <button className="px-2 py-1 text-xs bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center gap-1">
                <span>Run</span>
                <span>▶</span>
              </button>
            </div>
          </div>
          <div className="flex-1 p-4 font-mono text-sm relative">
            <pre className="whitespace-pre-wrap text-gray-800">
              <code>{typedCode}</code>
            </pre>
            {currentLine < codeLines.length && (
              <span className="inline-block w-2 h-4 bg-primary/80 animate-pulse ml-1" />
            )}
          </div>
          <div className="border-t px-4 py-1.5 bg-gray-50/50">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>Python 3.11</span>
                <span>•</span>
                <span>UTF-8</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================
// Simulated Agent Panel Component
// =============================================

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SimulatedAgentPanel = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'user',
      content: 'Create a line chart showing monthly sales trends'
    },
    {
      role: 'assistant',
      content: `I'll create an interactive line chart to visualize your monthly sales:

\`\`\`python
import pandas as pd
import plotly.express as px
import preswald

df = pd.read_csv('data/sales.csv')
monthly = df.groupby('date')['revenue'].sum()

fig = px.line(monthly, title='Monthly Sales')
preswald.plotly(fig)
\`\`\``
    },
    {
      role: 'user',
      content: 'Break it down by product category'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(2);
  
  const conversationFlow: Message[] = [
    {
      role: 'assistant',
      content: `I'll update the chart to show trends by category:

\`\`\`python
sales_by_category = df.groupby(['date', 'category'])['revenue'].sum()

fig = px.line(sales_by_category, 
              color='category',
              title='Sales by Category')
preswald.plotly(fig)
\`\`\``
    }
  ];

  // Effect to animate through the conversation
  useEffect(() => {
    if (currentMessageIndex < conversationFlow.length) {
      const timer = setTimeout(() => {
        setIsTyping(true);
        
        const messageTimer = setTimeout(() => {
          setCurrentMessageIndex(prev => prev + 1);
          setMessages(prev => [...prev, conversationFlow[currentMessageIndex]]);
          setIsTyping(false);
        }, 1000);

        return () => clearTimeout(messageTimer);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      // Reset after a delay when reaching the end
      const resetTimer = setTimeout(() => {
        setMessages([
          {
            role: 'user',
            content: 'Create a line chart showing monthly sales trends'
          },
          {
            role: 'assistant',
            content: `I'll create an interactive line chart to visualize your monthly sales:

\`\`\`python
import pandas as pd
import plotly.express as px
import preswald

df = pd.read_csv('data/sales.csv')
monthly = df.groupby('date')['revenue'].sum()

fig = px.line(monthly, title='Monthly Sales')
preswald.plotly(fig)
\`\`\``
          },
          {
            role: 'user',
            content: 'Break it down by product category'
          }
        ]);
        setCurrentMessageIndex(0);
      }, 3000);

      return () => clearTimeout(resetTimer);
    }
  }, [currentMessageIndex]);

  return (
    <div className="h-full flex flex-col">
      <motion.div 
        className="flex h-12 items-center justify-between border-b px-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center space-x-3">
          <Bot className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">Waldo</span>
        </div>
      </motion.div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <AnimatePresence mode="popLayout">
          {messages.map((message, index) => (
            <motion.div
              key={`${index}-${message.content.substring(0, 20)}`}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
              transition={{ duration: 0.2 }}
              className={cn(
                "rounded-md px-2.5 py-1.5 text-xs border",
                message.role === 'user'
                  ? "bg-gray-50 text-gray-900 border-gray-200"
                  : "bg-white text-gray-900 border-gray-200 shadow-sm"
              )}
            >
              <div className="whitespace-pre-wrap">
                {message.content.split('```').map((part, i) => {
                  if (i % 2 === 1) { // Code block
                    return (
                      <motion.div 
                        key={i} 
                        className="my-2 p-2 bg-gray-50 rounded-md font-mono"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {part.replace(/^python\n/, '')}
                      </motion.div>
                    );
                  }
                  return <p key={i}>{part}</p>;
                })}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isTyping && (
          <motion.div 
            className="flex items-center gap-1.5 text-xs text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex items-center space-x-0.5">
              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse [animation-delay:-0.15s]"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse [animation-delay:-0.3s]"></div>
            </div>
          </motion.div>
        )}
      </div>
      <motion.div 
        className="border-t p-3"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          <textarea
            placeholder="Ask Waldo to help you analyze your data..."
            className="w-full pr-8 py-1.5 px-2.5 rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-200 resize-none text-xs placeholder:text-gray-400 text-gray-900 transition-all duration-200 hover:border-gray-300"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            style={{ minHeight: '32px', maxHeight: '120px' }}
          />
          <motion.button
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Send className="w-3 h-3" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

// =============================================
// Simulated Projects Dashboard Component
// =============================================

const SimulatedProjectsDashboard = () => {
  const projects = [
    {
      id: 1,
      title: 'Sales Performance Dashboard',
      description: 'Interactive dashboard showing sales trends and KPIs',
      lastUpdated: '2 hours ago',
      collaborators: 3,
      stars: 12,
      isCompleted: true,
      status: 'published',
      icon: '📊',
    },
    {
      id: 2,
      title: 'Customer Analytics Report',
      description: 'Deep dive into customer behavior and segmentation',
      lastUpdated: '1 day ago',
      collaborators: 5,
      stars: 8,
      isCompleted: true,
      status: 'published',
      icon: '📈',
    },
    {
      id: 3,
      title: 'Marketing Campaign Analysis',
      description: 'ROI analysis of recent marketing campaigns',
      lastUpdated: '3 days ago',
      collaborators: 2,
      stars: 5,
      isCompleted: false,
      status: 'draft',
      icon: '🎯',
    },
    {
      id: 4,
      title: 'Inventory Management',
      description: 'Real-time inventory tracking and forecasting',
      lastUpdated: '1 week ago',
      collaborators: 4,
      stars: 15,
      isCompleted: false,
      status: 'draft',
      icon: '📦',
    },
  ];

  return (
    <div className="h-full">
      <div className="flex justify-between items-center p-2 bg-white border-b">
        <div className="flex gap-2">
          <button className="px-2 py-1 text-sm rounded-md border bg-gray-100 border-gray-300 font-medium text-gray-900">
            All
          </button>
          <button className="px-2 py-1 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors">
            Published
          </button>
          <button className="px-2 py-1 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors">
            Drafts
          </button>
        </div>
        <div className="flex gap-2">
          <button className="px-2 py-1 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors">
            Quick Create
          </button>
          <button className="px-2 py-1 text-sm bg-gray-900 text-white rounded-md font-medium hover:bg-gray-800 transition-colors">
            New Project
          </button>
        </div>
      </div>

      <div className="p-2 overflow-y-auto">
        <div className="grid grid-cols-2 gap-2">
          {projects.map(project => (
            <div
              key={project.id}
              className="group relative transition-all duration-200 hover:scale-[1.02] hover:shadow-md"
            >
              <div className="h-full border border-gray-200 rounded-lg p-3 hover:border-primary/50 transition-colors bg-white">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xl flex-shrink-0">{project.icon}</span>
                    <h3 className="font-medium text-gray-900 truncate text-sm">{project.title}</h3>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {project.status === 'draft' && (
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 font-medium rounded-full border border-gray-200 whitespace-nowrap">
                        Draft
                      </span>
                    )}
                    {project.isCompleted && (
                      <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-600 rounded-full border border-green-100 whitespace-nowrap">
                        Completed
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">{project.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{project.lastUpdated}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span>{project.collaborators}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    <span>{project.stars}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================
// Simulated Live Preview Component
// =============================================

const SimulatedLivePreview = () => {
  const [timeRange, setTimeRange] = useState(6);

  const revenueData = [
    { name: 'Jan', value: 4000 },
    { name: 'Feb', value: 3000 },
    { name: 'Mar', value: 5000 },
    { name: 'Apr', value: 2780 },
    { name: 'May', value: 1890 },
    { name: 'Jun', value: 2390 },
  ];

  const categoryData = [
    { name: 'Electronics', value: 4000 },
    { name: 'Furniture', value: 3000 },
    { name: 'Clothing', value: 2000 },
    { name: 'Accessories', value: 2780 },
  ];

  const customerGrowthData = [
    { name: 'Jan', value: 100 },
    { name: 'Feb', value: 150 },
    { name: 'Mar', value: 200 },
    { name: 'Apr', value: 250 },
    { name: 'May', value: 300 },
    { name: 'Jun', value: 350 },
  ];

  const marketShareData = [
    { name: 'Our Company', value: 35 },
    { name: 'Competitor A', value: 25 },
    { name: 'Competitor B', value: 20 },
    { name: 'Others', value: 20 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4">
        <h1 className="text-xl font-bold mb-2">Sales Performance Analysis</h1>
        <p className="text-sm text-gray-600">An analysis of our Q2 sales performance and key metrics</p>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-base font-medium mb-2">Monthly Revenue Trend</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-base font-medium mb-2">Revenue by Category</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#82ca9d" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          <h3 className="text-base font-medium mb-2">Time Range: {timeRange} months</h3>
          <input
            type="range"
            min="3"
            max="12"
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-base font-medium mb-2">Customer Growth</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={customerGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#ff7300" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-base font-medium mb-2">Market Share</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={marketShareData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {marketShareData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h2 className="text-lg font-semibold mb-2">Key Findings</h2>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>Revenue growth of 15% compared to Q1</li>
            <li>Electronics category showing strongest performance</li>
            <li>New customer acquisition up by 25%</li>
          </ul>
        </div>

        <div className="mt-4">
          <h2 className="text-lg font-semibold mb-2">Recommendations</h2>
          <div className="bg-blue-50 rounded-lg p-3">
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
              <li>Increase inventory for Electronics category</li>
              <li>Launch targeted marketing campaign for Furniture</li>
              <li>Consider seasonal promotions for Clothing</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================
// Simulated Team Collaboration Component
// =============================================

const SimulatedTeamCollaboration = () => {
  const collaborators = [
    { id: 1, name: 'Sarah Chen', role: 'Data Scientist', avatar: '👩‍🔬', status: 'online' },
    { id: 2, name: 'Mike Johnson', role: 'Developer', avatar: '👨‍💻', status: 'offline' },
    { id: 3, name: 'Emma Davis', role: 'Analyst', avatar: '👩‍💼', status: 'online' },
  ];

  const activities = [
    { id: 1, user: 'Sarah Chen', action: 'Updated sales dashboard', time: '2 min ago', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 2, user: 'Mike Johnson', action: 'Created new branch', time: '15 min ago', icon: <GitBranch className="w-4 h-4" /> },
    { id: 3, user: 'Emma Davis', action: 'Added comments', time: '1 hour ago', icon: <MessageSquare className="w-4 h-4" /> },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="border-b p-4">
        <h2 className="text-lg font-semibold mb-2">Team Workspace</h2>
        <div className="flex items-center gap-2">
          {collaborators.map(user => (
            <div key={user.id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border">
              <span className="text-lg">{user.avatar}</span>
              <span className="text-sm font-medium">{user.name}</span>
              <span className={`w-2 h-2 rounded-full ${user.status === 'online' ? 'bg-green-500' : 'bg-gray-300'}`} />
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex-1 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h3>
        <div className="space-y-3">
          {activities.map(activity => (
            <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="p-2 bg-white rounded-md border">
                {activity.icon}
              </div>
              <div>
                <p className="text-sm font-medium">{activity.user}</p>
                <p className="text-xs text-gray-600">{activity.action}</p>
                <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================
// Simulated Export & Share Component
// =============================================

const SimulatedExportShare = () => {
  const exportOptions = [
    { id: 1, title: 'Static Website', description: 'Export as a standalone website', icon: <Upload className="w-4 h-4" /> },
    { id: 2, title: 'Embed Code', description: 'Get code to embed in your site', icon: <Share2 className="w-4 h-4" /> },
    { id: 3, title: 'Download', description: 'Download as a ZIP archive', icon: <Download className="w-4 h-4" /> },
  ];

  return (
    <div className="h-full p-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Export Options</h2>
        <p className="text-sm text-gray-600">Choose how you want to share your project</p>
      </div>

      <div className="grid gap-4">
        {exportOptions.map(option => (
          <div key={option.id} className="p-4 border rounded-lg hover:border-gray-400 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-md">
                {option.icon}
              </div>
              <div>
                <h3 className="font-medium">{option.title}</h3>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-2">Share Link</h3>
        <div className="flex gap-2">
          <input 
            type="text" 
            value="https://preswald.com/share/dashboard-xyz"
            readOnly
            className="flex-1 px-3 py-1.5 text-sm border rounded-md bg-white"
          />
          <button className="px-3 py-1.5 bg-gray-900 text-white rounded-md text-sm font-medium">
            Copy
          </button>
        </div>
      </div>
    </div>
  );
};

interface FeatureShowcaseProps {
  selectedFeatureIndex: number;
}

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({ selectedFeatureIndex }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={selectedFeatureIndex}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg border border-gray-200 p-4 h-[700px] overflow-hidden"
      >
        {selectedFeatureIndex === 0 && <SimulatedVisualEditor />}
        {selectedFeatureIndex === 1 && <SimulatedAgentPanel />}
        {selectedFeatureIndex === 2 && <SimulatedProjectsDashboard />}
        {selectedFeatureIndex === 3 && <SimulatedLivePreview />}
        {selectedFeatureIndex === 4 && <SimulatedTeamCollaboration />}
        {selectedFeatureIndex === 5 && <SimulatedExportShare />}
      </motion.div>
    </AnimatePresence>
  );
};

export default FeatureShowcase; 